import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { Alert, DataTable, Empty, Muted, PageHeader, Section } from '@/components/ui'
import { consultarIa, fetchIaHistorico } from './api'

export function IaRhPage() {
  const qc = useQueryClient()
  const [pergunta, setPergunta] = useState('')
  const [resposta, setResposta] = useState('')
  const [erro, setErro] = useState('')

  const hist = useQuery({ queryKey: ['ia', 'historico'], queryFn: fetchIaHistorico })

  const mut = useMutation({
    mutationFn: () => consultarIa(pergunta.trim()),
    onSuccess: async (res) => {
      const dados = (res as { dados?: { resposta?: string } }).dados
      setResposta(dados?.resposta || (res as { mensagem?: string }).mensagem || 'Sem resposta.')
      setErro('')
      await qc.invalidateQueries({ queryKey: ['ia', 'historico'] })
    },
    onError: (err) => {
      setErro(err instanceof ApiError ? err.message : 'Falha na consulta.')
      setResposta('')
    },
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!pergunta.trim()) return
    mut.mutate()
  }

  const rows = hist.data || []

  return (
    <section className="hi-page">
      <PageHeader
        title="Assistente IA"
        subtitle="Consulta executiva via API de inteligência. Painel offline completo no legado."
        actions={
          <a className="btn btn-secondary" href={legacyAppUrl('#/ia-rh')}>
            Legado
          </a>
        }
      />

      <Section title="Nova consulta">
        <form className="hi-stack" onSubmit={onSubmit}>
          <label className="hi-muted">
            Pergunta
            <textarea
              className="hi-filters"
              style={{ width: '100%', marginTop: 6 }}
              rows={3}
              value={pergunta}
              onChange={(e) => setPergunta(e.target.value)}
              placeholder="Ex.: Qual o turnover do mês?"
            />
          </label>
          {erro ? <Alert tone="danger">{erro}</Alert> : null}
          {resposta ? <Alert tone="success">{resposta}</Alert> : null}
          <button type="submit" className="btn btn-primary" disabled={mut.isPending}>
            {mut.isPending ? 'Consultando…' : 'Consultar'}
          </button>
        </form>
      </Section>

      <Section title="Histórico" flush>
        {hist.isLoading ? <Muted>Carregando…</Muted> : null}
        {hist.isError ? (
          <Alert tone="warning">
            Histórico indisponível para este perfil ou sem registros.
          </Alert>
        ) : null}
        {!hist.isLoading && rows.length === 0 ? <Empty>Sem consultas recentes.</Empty> : null}
        {rows.length > 0 ? (
          <DataTable>
            <thead>
              <tr>
                <th>Pergunta</th>
                <th>Categoria</th>
                <th>Quando</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 20).map((r, i) => (
                <tr key={String(r.id || i)}>
                  <td>{String(r.pergunta || '—')}</td>
                  <td>{String(r.categoria || '—')}</td>
                  <td>
                    {r.created_at
                      ? new Date(String(r.created_at)).toLocaleString('pt-BR')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : null}
      </Section>
    </section>
  )
}
